import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Modal, Button, Input } from '@/components/ui'
import { useCreateDeduction } from '@/features/security-ledger/hooks/useSecurityLedger'
import {
  deductionSchema,
  type DeductionFormValues,
} from '@/features/security-ledger/schemas/security-ledger.schemas'
import { BalancePreview } from './BalancePreview'

interface DeductionModalProps {
  companyId: string
  /** User document `_id` (Mongo ObjectId); sent as DTO `employeeId`. */
  targetUserId: string
  currentBalance: number
  currency: string
  open: boolean
  onClose: () => void
}

export function DeductionModal({
  companyId,
  targetUserId,
  currentBalance,
  currency,
  open,
  onClose,
}: DeductionModalProps): React.ReactElement | null {
  const mutation = useCreateDeduction()

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<DeductionFormValues>({
    resolver: zodResolver(deductionSchema),
    defaultValues: { employeeId: targetUserId, amount: undefined, note: '' },
  })

  const amount = watch('amount')

  useEffect(() => {
    if (open) {
      reset({ employeeId: targetUserId, amount: undefined, note: '' })
    }
  }, [open, targetUserId, reset])

  const onSubmit = (values: DeductionFormValues) => {
    mutation.mutate(
      { companyId, body: { type: 'deduction', employeeId: values.employeeId, amount: values.amount, note: values.note || undefined } },
      { onSuccess: () => { reset(); onClose() } },
    )
  }

  const handleClose = () => { reset(); onClose() }

  if (!open) return null

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Post manual deduction"
      footer={
        <div className="flex gap-2">
          <Button type="submit" form="deduction-form" variant="primary" loading={mutation.isPending}>
            {mutation.isPending ? 'Posting…' : 'Post deduction'}
          </Button>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
        </div>
      }
    >
      <form id="deduction-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <input type="hidden" {...register('employeeId')} />
        <Input
          {...register('amount', { valueAsNumber: true })}
          id="deduction-amount"
          label="Amount"
          type="number"
          step="0.01"
          min="0.01"
          required
          placeholder="0.00"
          error={errors.amount?.message}
        />
        <BalancePreview
          currentBalance={currentBalance}
          delta={amount}
          direction="increase"
          currency={currency}
        />
        <p className="text-xs text-amber-700 dark:text-amber-300">
          This applies immediately and notifies the employee by email.
        </p>
        <div>
          <label
            htmlFor="deduction-note"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Note
          </label>
          <textarea
            {...register('note')}
            id="deduction-note"
            rows={3}
            placeholder="Optional note…"
            className="box-border w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </div>
      </form>
    </Modal>
  )
}

DeductionModal.displayName = 'DeductionModal'
